import type { LucideIcon } from 'lucide-react';
import { BookOpen, Compass, FileText, GraduationCap, Mail, Wallet } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@ibas/ui';

import { ScrollReveal } from '@/components/motion';
import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { STAGGER } from '@/lib/motion-tokens';

/** Dwell time on each stage before the path advances to the next (ms). */
const STEP_INTERVAL_MS = 900;

/** A single stage in the IBAS admissions journey. */
export interface RoadmapStep {
  /** Stable identifier, also used as the React key. */
  key: string;
  /** Short stage name (e.g. "Explore"). */
  title: string;
  /** One-sentence description of what happens at this stage. */
  description: string;
  /** Decorative icon shown inside the stage marker. */
  icon?: LucideIcon;
}

export interface JourneyRoadmapProps {
  /** Small uppercase eyebrow above the heading. */
  eyebrow?: string;
  /** Section heading. */
  title?: string;
  /** Supporting sentence under the heading. */
  description?: string;
  /** The ordered stages of the journey. Defaults to the six IBAS admissions stages. */
  steps?: RoadmapStep[];
  /**
   * Index of the current/most-advanced stage (0-based). Stages at or before this
   * index render as done/active (`bg-primary`); later stages render as upcoming
   * (`bg-muted`). Default 0 — "Explore" is the entry point for a new visitor.
   */
  activeIndex?: number;
  className?: string;
}

/** The six real stages a prospective student moves through, in order. */
const DEFAULT_STEPS: RoadmapStep[] = [
  {
    key: 'explore',
    title: 'Explore',
    description: 'Find your programme and connect with an IBAS advisor to plan your path.',
    icon: Compass,
  },
  {
    key: 'apply',
    title: 'Apply',
    description: 'Submit your application and supporting documents for your chosen programme.',
    icon: FileText,
  },
  {
    key: 'offer',
    title: 'Offer',
    description: 'Receive your admission decision from the IBAS admissions team.',
    icon: Mail,
  },
  {
    key: 'enrol',
    title: 'Enrol',
    description: 'Confirm your place by completing enrolment and settling your fees.',
    icon: Wallet,
  },
  {
    key: 'study',
    title: 'Study',
    description: 'Begin your studies — online, on-site or blended, at your own pace.',
    icon: BookOpen,
  },
  {
    key: 'graduate',
    title: 'Graduate',
    description: 'Graduate and progress globally, backed by Swiss quality.',
    icon: GraduationCap,
  },
];

/**
 * JourneyRoadmap — the six-stage IBAS admissions journey, from first enquiry to
 * graduation, rendered as a connected horizontal timeline (stacking to a vertical
 * list on small screens).
 *
 * Motion intent: the stage markers and cards are revealed as one connected chain
 * via `ScrollReveal`'s `stagger` — each stage rises into place a beat after the
 * last, so the path itself feels drawn open in sequence rather than dumped on
 * screen. The connecting line and "done" markers use `bg-primary`; upcoming
 * stages sit in quiet `bg-muted`, so the chain also reads as *progress*, not just
 * a list.
 *
 * Auto-run: once the section scrolls into view, an internal `step` state walks
 * itself from `activeIndex` to the final stage on a timer (see
 * `STEP_INTERVAL_MS`), so the `bg-primary` connecting line fills progressively
 * and each marker flips from upcoming → done in sequence — the path draws
 * itself. Under `prefers-reduced-motion: reduce` we skip the timer entirely and
 * render the completed path at once (all markers done, line fully drawn), so
 * opt-out users still get a clear, finished, legible roadmap.
 */
export function JourneyRoadmap({
  eyebrow = 'How it works',
  title = 'Your admissions journey',
  description = 'Six stages from first enquiry to graduation — the same path every IBAS student follows.',
  steps = DEFAULT_STEPS,
  activeIndex = 0,
  className,
}: JourneyRoadmapProps) {
  const count = steps.length;
  const columnPct = count > 0 ? 100 / count : 0;
  const lineInset = columnPct / 2;

  // Base/initial stage from the prop, clamped into range.
  const baseIndex = Math.max(0, Math.min(activeIndex, count - 1));

  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  // Rect-based reveal — safe inside preserve-3d ancestors, and short-circuited
  // (reports true immediately) under reduced motion.
  const inView = useInView(sectionRef, { once: true, disabled: reduced });

  // Internal active stage that self-advances; seeded from the base index.
  const [activeStep, setActiveStep] = useState(baseIndex);

  useEffect(() => {
    // Reduced motion draws the finished state without a timer (handled below);
    // otherwise wait until the section is on-screen before the path runs.
    if (reduced || !inView) return;

    const id = setInterval(() => {
      setActiveStep((prev) => {
        if (prev >= count - 1) {
          clearInterval(id);
          return prev;
        }
        return prev + 1;
      });
    }, STEP_INTERVAL_MS);

    return () => clearInterval(id);
  }, [reduced, inView, count]);

  // Effective active stage driving every "done" decision below. Reduced motion
  // jumps straight to the completed path.
  const active = reduced ? count - 1 : activeStep;
  const progressPct = Math.max(0, Math.min(active, count - 1)) * columnPct;

  return (
    <section
      ref={sectionRef}
      className={cn(
        'flex min-h-[100svh] flex-col justify-center bg-background py-12 sm:py-16',
        className,
      )}
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            {eyebrow}
          </p>
          <h2 className="mt-3 font-display text-4xl font-semibold text-foreground sm:text-5xl">
            {title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{description}</p>
        </div>

        <div className="relative mt-10 lg:mt-12">
          {/* Decorative connecting line — desktop only, sits behind the markers. */}
          <div
            className="pointer-events-none absolute inset-x-0 top-6 z-0 hidden lg:block"
            aria-hidden="true"
          >
            <div
              className="absolute h-px bg-muted"
              style={{ left: `${lineInset}%`, right: `${lineInset}%` }}
            />
            <div
              className="absolute h-px bg-primary transition-[width] duration-700 ease-out"
              style={{ left: `${lineInset}%`, width: `${progressPct}%` }}
            />
          </div>

          <ScrollReveal
            as="ol"
            stagger={STAGGER.base}
            className="relative grid grid-cols-1 gap-6 lg:grid-cols-6 lg:gap-5"
            aria-label="Admissions journey stages"
          >
            {steps.map((step, i) => {
              const done = i <= active;
              const Icon = step.icon;
              // The marker the path is currently lighting up — gets a brief
              // emphasis pulse as it flips to done (skipped under reduced motion).
              const activating = !reduced && inView && i === active && active < count - 1;

              return (
                <li
                  key={step.key}
                  className="relative flex gap-4 text-left lg:flex-col lg:items-start lg:gap-0"
                >
                  <div className="flex flex-col items-center lg:w-full">
                    <span
                      className={cn(
                        'relative z-10 flex size-12 shrink-0 items-center justify-center rounded-full border-2 transition-[color,background-color,border-color,transform,box-shadow] duration-300',
                        done
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-muted text-muted-foreground',
                        activating && 'scale-110 ring-4 ring-primary/25',
                      )}
                    >
                      {Icon ? (
                        <Icon className="size-5" aria-hidden="true" strokeWidth={1.75} />
                      ) : (
                        <span className="font-label text-sm font-semibold">{i + 1}</span>
                      )}
                    </span>
                    {i < count - 1 ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-2 w-px flex-1 transition-colors duration-500 lg:hidden',
                          i < active ? 'bg-primary' : 'bg-muted',
                        )}
                      />
                    ) : null}
                  </div>

                  <Card
                    className={cn(
                      'flex-1 gap-2 py-4 lg:mt-4 lg:w-full',
                      done ? 'border-primary/30' : undefined,
                    )}
                  >
                    <CardHeader className="gap-1">
                      <Badge
                        variant={done ? 'default' : 'outline'}
                        className="w-fit font-label text-[10px] tracking-[0.18em] uppercase"
                      >
                        Step {i + 1} of {count}
                      </Badge>
                      <CardTitle className="font-display text-xl">{step.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
